import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, Switch, Space, message, Popconfirm, Alert, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import PersianDatePicker from './common/PersianDatePicker';

/**
 * مدیریت زمان‌های حرکت هاگوارتز اکسپرس — جایگزین منطق قدیمیِ هاردکد «همیشه
 * جمعه ساعت ۱۱». هر دعوت‌نامه به یکی از این زمان‌ها متصل می‌شود.
 */
export default function TrainScheduleManager() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getTrainSchedules();
      if (res.data.success) setSchedules(res.data.schedules || []);
    } catch {
      message.error('خطا در بارگذاری زمان‌های حرکت');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ platform: '9¾', capacity: 500, isActive: true });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      label: record.label,
      departureAt: record.departureAt,
      platform: record.platform,
      capacity: record.capacity,
      isActive: record.isActive
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, departureAt: new Date(values.departureAt).toISOString() };
      if (editing) {
        await adminAPI.updateTrainSchedule(editing._id, payload);
        message.success('زمان حرکت ویرایش شد');
      } else {
        await adminAPI.createTrainSchedule(payload);
        message.success('زمان حرکت جدید ایجاد شد');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      if (err?.errorFields) return; // validation error — already shown by form
      message.error('خطا در ذخیره‌سازی');
    }
  };

  const remove = async (id) => {
    try {
      await adminAPI.deleteTrainSchedule(id);
      message.success('حذف شد');
      load();
    } catch {
      message.error('خطا در حذف');
    }
  };

  const columns = [
    { title: 'برچسب', dataIndex: 'label' },
    { title: 'زمان حرکت', dataIndex: 'departureAt', render: (v) => new Date(v).toLocaleString('fa-IR') },
    { title: 'سکو', dataIndex: 'platform' },
    { title: 'ظرفیت', dataIndex: 'capacity', render: (v) => v ?? 'نامحدود' },
    {
      title: 'رزرو شده',
      key: 'bookedCount',
      render: (_, r) => `${r.bookedCount ?? 0}${r.capacity != null ? ` / ${r.capacity}` : ''}`
    },
    {
      title: 'وضعیت ظرفیت',
      key: 'isFull',
      render: (_, r) => (r.capacity == null ? '—' : r.isFull ? <Tag color="red">پر شده</Tag> : <Tag color="green">{r.remainingCapacity} جای خالی</Tag>)
    },
    { title: 'فعال', dataIndex: 'isActive', render: (v) => (v ? '✅' : '⛔') },
    {
      title: '', key: 'action', render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="حذف این زمان حرکت؟" onConfirm={() => remove(r._id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="زمان‌های حرکت هاگوارتز اکسپرس"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>زمان جدید</Button>}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="هر دعوت‌نامه هنگام ایجاد به یکی از این زمان‌ها متصل می‌شود و بعداً هم قابل تغییر است (از بخش دعوت‌نامه‌ها)."
      />
      <Table rowKey="_id" loading={loading} columns={columns} dataSource={schedules} pagination={false} />

      <Modal
        title={editing ? 'ویرایش زمان حرکت' : 'زمان حرکت جدید'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="ذخیره"
        cancelText="انصراف"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="label" label="برچسب (مثلا «جمعه ۱۱:۰۰ صبح»)" rules={[{ required: true, message: 'الزامی است' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="departureAt" label="تاریخ و ساعت حرکت" rules={[{ required: true, message: 'الزامی است' }]}>
            <PersianDatePicker withTime />
          </Form.Item>
          <Form.Item name="platform" label="سکو">
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label="ظرفیت (پیش‌فرض ۵۰۰ نفر — خالی = نامحدود)">
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
